/** Course preview page for learners who are browsing before subscribing. */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { BookOpen, ChevronDown, Clock3, Layers3, Lock, ScrollText, Sparkles } from 'lucide-react';
import type { CourseWithContent, ModuleWithContent } from '@/shared/api/hooks/useCourses';
import { Badge, Button, PageContainer, Surface, Tag } from '@/interfaces/learner/ui';
import CourseActionPanel from './CourseActionPanel';
import CourseHeroHeader from './CourseHeroHeader';
import {
  countPublishedLessons,
  countPublishedTopics,
  estimateCourseTime,
  filterPublishedModules,
} from './courseContent';

const EASE = [0.16, 1, 0.3, 1] as const;

interface PreSubscriptionPageProps {
  course: CourseWithContent;
}

export default function PreSubscriptionPage({ course }: PreSubscriptionPageProps) {
  const navigate = useNavigate();
  const modules = filterPublishedModules(course.modules ?? []);
  const categories = course.categories ?? [];
  const tags = course.tags ?? [];
  const lessonCount = countPublishedLessons(modules);
  const topicCount = countPublishedTopics(modules);
  const moduleCount = modules.length;
  const estimatedTime = lessonCount > 0 ? estimateCourseTime(lessonCount) : null;
  const accent = course.accentColour ?? 'var(--color-accent)';
  const isSingleModule = moduleCount === 1;
  const handleSubscribe = () => undefined;

  return (
    <PageContainer className="relative pb-24">
      <section className="relative" style={{ paddingTop: 'var(--page-top-spacing)' }}>
        <div className="grid gap-8 learner-double:grid-cols-[minmax(0,1fr)_minmax(380px,var(--learner-context-rail-width))]">
          <div className="min-w-0">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.05, ease: EASE }}
            >
              <CourseHeroHeader
                backLink={{ to: '/discover', label: 'Back to Discover' }}
                eyebrow={`Course Preview · ${lessonCount} lessons`}
                title={course.title}
                badges={[
                  ...categories.map((category) => ({
                    key: `category-${category.id}`,
                    label: category.name,
                    variant: 'accent' as const,
                  })),
                  ...(moduleCount > 0
                    ? [
                        {
                          key: 'modules',
                          label: `${moduleCount} modules`,
                          variant: 'surface' as const,
                        },
                      ]
                    : []),
                  ...(topicCount > 0
                    ? [
                        {
                          key: 'topics',
                          label: `${topicCount} topics`,
                          variant: 'surface' as const,
                        },
                      ]
                    : []),
                  ...(estimatedTime
                    ? [{ key: 'time', label: estimatedTime, variant: 'surface' as const }]
                    : []),
                ]}
                summary="Preview the curriculum, browse the topic structure, and see what's included before you subscribe."
                description={course.description}
                tags={tags.map((tag) => (
                  <Tag key={tag.id}>{tag.name}</Tag>
                ))}
              />
            </motion.div>

            <motion.div
              className="mt-7"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1, ease: EASE }}
            >
              <Surface tone="solid" padding={0}>
                <div className="relative overflow-hidden">
                  {course.headerImageUrl ? (
                    <img
                      src={course.headerImageUrl}
                      alt=""
                      className="h-52 w-full object-cover learner-single:h-60"
                    />
                  ) : (
                    <div
                      className="flex h-52 w-full items-center justify-center learner-single:h-60"
                      style={{
                        background: `linear-gradient(135deg, color-mix(in srgb, ${accent} 34%, white) 0%, color-mix(in srgb, ${accent} 14%, var(--color-surface-solid)) 52%, var(--color-surface-solid) 100%)`,
                      }}
                    >
                      <BookOpen size={46} style={{ color: accent, opacity: 0.55 }} />
                    </div>
                  )}

                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.08) 100%)',
                    }}
                  />
                </div>

                <div className="p-6">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <Badge variant="accent">Preview access</Badge>
                    <Badge variant="muted">Browse before subscribing</Badge>
                  </div>

                  <p className="text-panel-title mt-4">
                    A clear look at the course before you commit.
                  </p>

                  <p className="text-body mt-2" style={{ maxWidth: 'var(--measure-panel-copy)' }}>
                    You can review the module structure, inspect the lesson flow, and understand the
                    size of the course without unlocking the learning session yet.
                  </p>
                </div>
              </Surface>
            </motion.div>
          </div>

          <motion.aside
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.12, ease: EASE }}
            className="learner-double:pt-10"
          >
            <div className="learner-double:sticky learner-double:top-[var(--sticky-top-offset)]">
              <CourseActionPanel
                eyebrow="Subscribe to Start"
                title="Unlock the full course"
                description="Subscribe to open every published lesson in this course and move from preview into the full learner flow."
                badges={[
                  { key: 'lessons', label: `${lessonCount} lessons`, variant: 'accent' },
                  ...(topicCount > 0
                    ? [{ key: 'topics', label: `${topicCount} topics`, variant: 'muted' as const }]
                    : []),
                  ...(estimatedTime
                    ? [{ key: 'time', label: estimatedTime, variant: 'muted' as const }]
                    : []),
                ]}
                tone="neutral"
              >
                <div className="space-y-3">
                  <PreviewBenefit
                    icon={<ScrollText size={15} />}
                    text="Open the full curriculum and every lesson in sequence."
                  />
                  <PreviewBenefit
                    icon={<Layers3 size={15} />}
                    text="Keep the module and topic structure exactly as previewed here."
                  />
                  <PreviewBenefit
                    icon={<Clock3 size={15} />}
                    text="Start now and return later with the learner shell experience intact."
                  />
                </div>

                <div className="mt-6 flex flex-col gap-2.5">
                  <Button
                    variant="primary"
                    size="xl"
                    icon={<Sparkles size={18} />}
                    onPress={handleSubscribe}
                    style={{
                      width: '100%',
                      justifyContent: 'center',
                    }}
                  >
                    Subscribe to Start Learning
                  </Button>

                  <Button
                    variant="secondary"
                    size="md"
                    onPress={() => navigate('/discover')}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    Browse More Courses
                  </Button>
                </div>
              </CourseActionPanel>
            </div>
          </motion.aside>
        </div>
      </section>

      <motion.section
        className="relative mt-8"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.16, ease: EASE }}
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-section-title">What you&apos;ll unlock</h2>
            <p className="text-body-muted mt-1">
              {moduleCount > 0
                ? `${moduleCount} modules arranged into ${topicCount} topics and ${lessonCount} lessons.`
                : 'Curriculum details are being prepared for this course.'}
            </p>
          </div>

          <Badge variant="surface">Preview mode</Badge>
        </div>

        {moduleCount > 0 ? (
          <div className="mt-6 flex flex-col gap-4">
            {modules.map((module, index) => (
              <PreviewModule
                key={module.id}
                module={module}
                index={index}
                hideModuleHeader={isSingleModule}
              />
            ))}
          </div>
        ) : (
          <Surface className="mt-6" padding={24}>
            <p className="text-panel-title">Curriculum details coming soon</p>
            <p className="text-body-muted mt-1">
              This course exists, but the published module structure is still being prepared.
            </p>
          </Surface>
        )}
      </motion.section>
    </PageContainer>
  );
}

function PreviewBenefit({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <Surface tone="muted" padding={12}>
      <div className="flex items-start gap-3">
        <span
          className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
          style={{
            background: 'var(--color-accent-subtle)',
            color: 'var(--color-accent-text)',
          }}
        >
          {icon}
        </span>
        <p className="text-body">{text}</p>
      </div>
    </Surface>
  );
}

function PreviewModule({
  module,
  index,
  hideModuleHeader,
}: {
  module: ModuleWithContent;
  index: number;
  hideModuleHeader: boolean;
}) {
  const topics = module.topics ?? [];
  const topicLessonCounts = topics.map((topic) => topic.lessons?.length ?? 0);
  const totalLessons = topicLessonCounts.reduce((sum, count) => sum + count, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.22 + index * 0.05, ease: EASE }}
    >
      <Surface tone="solid" padding={0}>
        {!hideModuleHeader && (
          <div className="flex items-center justify-between gap-4 px-5 py-4">
            <div className="min-w-0">
              <p className="text-eyebrow" style={{ color: 'var(--color-text-muted)' }}>
                Module {index + 1}
              </p>
              <h3 className="text-card-title mt-1 truncate">{module.title}</h3>
            </div>

            {totalLessons > 0 && (
              <Badge variant="muted">
                {totalLessons} {totalLessons === 1 ? 'lesson' : 'lessons'}
              </Badge>
            )}
          </div>
        )}

        <div
          className={hideModuleHeader ? 'px-4 py-4' : 'border-t px-4 py-4'}
          style={{ borderColor: 'var(--color-surface-border)' }}
        >
          <div className="flex flex-col gap-2">
            {topics.map((topic, topicIndex) => (
              <PreviewTopic
                key={topic.id}
                topic={topic}
                lessonCount={topicLessonCounts[topicIndex]}
                defaultExpanded={hideModuleHeader && topicIndex === 0}
              />
            ))}
          </div>
        </div>
      </Surface>
    </motion.div>
  );
}

interface TopicData {
  id: string;
  title: string;
  lessons?: Array<{
    id: string;
    title: string;
    description?: string | null;
    isLive?: boolean;
  }>;
}

function PreviewTopic({
  topic,
  lessonCount,
  defaultExpanded,
}: {
  topic: TopicData;
  lessonCount: number;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(Boolean(defaultExpanded));
  const liveLessons = topic.lessons ?? [];
  const hasLessons = liveLessons.length > 0;

  return (
    <div>
      <button
        type="button"
        onClick={() => hasLessons && setExpanded((prev) => !prev)}
        className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition-colors"
        style={{
          background: expanded ? 'var(--color-surface-muted)' : 'transparent',
          cursor: hasLessons ? 'pointer' : 'default',
        }}
        aria-expanded={hasLessons ? expanded : undefined}
      >
        {hasLessons ? (
          <ChevronDown
            size={16}
            className={`shrink-0 transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'}`}
            style={{ color: 'var(--color-text-muted)' }}
          />
        ) : (
          <Lock size={14} className="shrink-0" style={{ color: 'var(--color-text-muted)' }} />
        )}

        <div className="min-w-0 flex-1">
          <p className="text-list-title truncate">{topic.title}</p>
        </div>

        {lessonCount > 0 && (
          <span className="text-list-meta-muted whitespace-nowrap">
            {lessonCount} {lessonCount === 1 ? 'lesson' : 'lessons'}
          </span>
        )}
      </button>

      <AnimatePresence initial={false}>
        {expanded && hasLessons && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: EASE }}
            style={{ overflow: 'hidden' }}
          >
            <div
              className="ml-7 mt-2 flex flex-col gap-2 border-l pl-4"
              style={{ borderColor: 'var(--color-border)' }}
            >
              {liveLessons.map((lesson, index) => (
                <Surface key={lesson.id} tone="muted" padding={12}>
                  <div className="flex items-start gap-3">
                    <span
                      className="text-list-meta mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full tabular-nums"
                      style={{
                        color: 'var(--color-text-muted)',
                        background: 'var(--color-surface-solid)',
                        border: '1px solid var(--color-surface-border)',
                      }}
                    >
                      {index + 1}
                    </span>

                    <div className="min-w-0 flex-1">
                      <p className="text-list-title truncate">{lesson.title}</p>
                      {lesson.description && (
                        <p className="text-list-meta-muted mt-1 line-clamp-2">
                          {lesson.description}
                        </p>
                      )}
                    </div>

                    <Lock
                      size={13}
                      className="mt-1 shrink-0"
                      style={{ color: 'var(--color-text-muted)', opacity: 0.7 }}
                    />
                  </div>
                </Surface>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
